<script lang="ts">
	import { onMount } from 'svelte';
	import { boatsApi, type Boat } from '../api';
	import { userStore } from '../stores/user';
	import BoatDrawer from '../components/BoatDrawer.svelte';
	import BoatDetailsDrawer from '../components/BoatDetailsDrawer.svelte';
	import ConfirmationDrawer from '../components/ConfirmationDrawer.svelte';
	import BoatCard from '../components/BoatCard.svelte';
	import PageHeader from '../components/PageHeader.svelte';
	import LoadingSpinner from '../components/LoadingSpinner.svelte';
	import ErrorMessage from '../components/ErrorMessage.svelte';
	import EmptyState from '../components/EmptyState.svelte';

	let boats = $state<Boat[]>([]);
	let loading = $state(true);
	let error = $state<string | null>(null);
	let isBoatDrawerOpen = $state(false);
	let isBoatDetailsDrawerOpen = $state(false);
	let selectedBoat = $state<Boat | null>(null);
	let viewBoat = $state<Boat | null>(null);
	let isConfirmationDrawerOpen = $state(false);
	let boatToDelete = $state<Boat | null>(null);
	let isDeleting = $state(false);

	function openBoatModal() {
		selectedBoat = null;
		isBoatDrawerOpen = true;
	}

	function openEditModal(boat: Boat) {
		selectedBoat = boat;
		isBoatDrawerOpen = true;
	}

	function closeBoatModal() {
		isBoatDrawerOpen = false;
		selectedBoat = null;
	}

	function openViewDetailsModal(boat: Boat) {
		viewBoat = boat;
		isBoatDetailsDrawerOpen = true;
	}

	function closeViewDetailsModal() {
		isBoatDetailsDrawerOpen = false;
		viewBoat = null;
	}

	function handleEditFromDetails(boat: Boat) {
		// Close the details drawer and open the edit drawer
		closeViewDetailsModal();
		openEditModal(boat);
	}

	function handleDeleteFromDetails(boat: Boat) {
		// Close the details drawer and open delete confirmation
		closeViewDetailsModal();
		openDeleteConfirmation(boat);
	}

	function openDeleteConfirmation(boat: Boat) {
		boatToDelete = boat;
		isConfirmationDrawerOpen = true;
	}

	function closeDeleteConfirmation() {
		isConfirmationDrawerOpen = false;
		boatToDelete = null;
	}

	async function handleDeleteConfirm() {
		if (!boatToDelete) return;

		try {
			isDeleting = true;
			const boatId = boatToDelete.id;
			await boatsApi.deleteBoat(boatId);

			// Remove the deleted boat from the local list
			boats = boats.filter((b) => b.id !== boatId);

			closeDeleteConfirmation();
		} catch (err) {
			console.error('Failed to delete boat:', err);
			error = err instanceof Error ? err.message : 'Failed to delete boat';
		} finally {
			isDeleting = false;
		}
	}

	async function handleBoatCreated() {
		// Refresh the boats list after creating a new boat
		await loadBoats();
	}

	async function loadBoats() {
		try {
			loading = true;
			error = null;
			const result = await boatsApi.getBoats();
			// Handle the paginated result
			boats = Array.isArray(result) ? result : result.data || [];
		} catch (err) {
			error = err instanceof Error ? err.message : 'Failed to load boats';
		} finally {
			loading = false;
		}
	}

	onMount(async () => {
		await loadBoats();
	});
</script>

<div class="container mx-auto px-4 py-8">
	<!-- Header -->
	<PageHeader
		title="All Boats"
		description="Manage your sailing fleet"
		actionText={$userStore ? 'Add Boat' : undefined}
		onAction={$userStore ? openBoatModal : undefined}
	/>

	{#if loading}
		<LoadingSpinner />
	{:else if error}
		<ErrorMessage message={error} onRetry={loadBoats} />
	{:else if boats.length === 0}
		<EmptyState
			icon="anchor"
			title="No boats found"
			description="Get started by adding your first boat to the system."
			actionText={$userStore ? 'Add Your First Boat' : undefined}
			onAction={$userStore ? openBoatModal : undefined}
		/>
	{:else}
		<div class="mt-8">
			<div class="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
				{#each boats as boat (boat.id)}
					<BoatCard
						{boat}
						onView={openViewDetailsModal}
						onEdit={$userStore ? openEditModal : undefined}
						onDelete={$userStore ? openDeleteConfirmation : undefined}
					/>
				{/each}
			</div>
		</div>
	{/if}
</div>

<!-- Boat Drawer (Create/Edit) -->
<BoatDrawer
	isOpen={isBoatDrawerOpen}
	boat={selectedBoat}
	onClose={closeBoatModal}
	onBoatCreated={handleBoatCreated}
/>

<!-- Boat Details Drawer -->
<BoatDetailsDrawer
	isOpen={isBoatDetailsDrawerOpen}
	boat={viewBoat}
	onClose={closeViewDetailsModal}
	onEdit={handleEditFromDetails}
	onDelete={handleDeleteFromDetails}
/>

<!-- Confirmation Drawer (Delete) -->
<ConfirmationDrawer
	isOpen={isConfirmationDrawerOpen}
	title="Delete Boat"
	message="Are you sure you want to delete {boatToDelete?.name}? This will remove the boat and all its associated data."
	confirmText={isDeleting ? 'Deleting...' : 'Delete Boat'}
	cancelText="Cancel"
	confirmButtonClass="bg-red-600 hover:bg-red-700 focus:ring-red-500"
	onConfirm={handleDeleteConfirm}
	onCancel={closeDeleteConfirmation}
/>
